/* apps/web/src/branding/brandingStore.ts
 *
 * The live deployment identity.
 *
 * `branding.config.ts` is what the deployment SHIPPED — the build-time
 * constant, still the single file a customer edits. This store is what the app
 * is CURRENTLY rendering, which is the same thing until the Branding & colors
 * screen previews a change.
 *
 * It exists because that screen is a live editor: the canvas's Branding tab
 * says "Applies live across every screen", and a colour written only onto
 * :root would change the chrome while the logo and the wordmark stayed put.
 * So the logo components read the identity from here rather than importing the
 * constant, and one `preview()` moves the whole product at once.
 *
 * Nothing here persists. A reload is back to the deployment's own identity,
 * because making a preview outlive the tab would be a deployment-wide setting
 * kept in one person's browser. Persisting it is the API's job — see the note
 * the Branding tab shows beside Save.
 */
import { useSyncExternalStore } from "react";
import { applyBranding } from "./applyBranding";
import { branding as shipped, type Branding } from "./branding.config";
import type { DeploymentBrandingSettings } from "@yazamco/shared-types";
import { fetchDeploymentBranding, updateDeploymentBranding } from "../lib/branding";

let current: Branding = shipped;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** The identity as shipped by this deployment — what "Reset" goes back to. */
export function shippedBranding(): Branding {
  return shipped;
}

export function currentBranding(): Branding {
  return current;
}

/**
 * Render `next` everywhere: the CSS custom properties, the logo components and
 * the document title, in one commit.
 */
export function previewBranding(next: Branding): void {
  current = next;
  applyBranding(next);
  emit();
}

/** Back to the deployment's own identity. */
export function resetBranding(): void {
  previewBranding(shipped);
}

function resolvedBranding(saved: DeploymentBrandingSettings | null): Branding {
  if (!saved) return shipped;
  /* One uploaded logo has to serve every slot. The Branding tab edits a single
     image, so a deployment that uploads its own mark and then still sees the
     shipped YazamcoOps logo on the login identity panel has not been rebranded
     — it has been half-rebranded. A custom mark therefore also becomes the
     full horizontal logo (and its reversed twin, which a custom upload never
     has); both are fitted by max-height/max-width, so a square mark in the
     wide login slot keeps its proportions. Untouched, every slot stays as
     shipped. */
  const customMark = saved.logoMark !== shipped.logoMark ? saved.logoMark : null;
  return {
    ...shipped,
    primaryColor: saved.primaryColor,
    secondaryColor: saved.secondaryColor,
    productName: saved.productName,
    logoMark: saved.logoMark,
    logoMarkLight: customMark ?? shipped.logoMarkLight,
    logo: customMark ?? shipped.logo,
    logoLight: customMark ?? shipped.logoLight,
  };
}

/** Load the deployment-wide identity and apply it. Public: called at boot,
 * before any session exists, so the sign-in screen is branded too. */
export async function loadPersistedBranding(): Promise<Branding> {
  const { branding } = await fetchDeploymentBranding();
  const next = resolvedBranding(branding);
  previewBranding(next);
  return next;
}

/** Persist the editable identity fields, then render the canonical response. */
export async function persistBranding(next: Branding): Promise<Branding> {
  const { branding } = await updateDeploymentBranding({
    primaryColor: next.primaryColor,
    secondaryColor: next.secondaryColor,
    productName: next.productName,
    logoMark: next.logoMark,
  });
  const resolved = resolvedBranding(branding);
  previewBranding(resolved);
  return resolved;
}

/** True while the screen is showing something other than what was shipped. */
export function brandingIsPreviewed(): boolean {
  return current !== shipped;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The identity to render. Re-renders the caller when a preview is applied. */
export function useBranding(): Branding {
  return useSyncExternalStore(subscribe, currentBranding, currentBranding);
}
